import { ceil, compose, isEmpty, map, reject, take } from 'lodash/fp';
import { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import {
    Badge,
    formatCountToMaxNinetyNine,
    IconButton,
    IconName,
    Stack,
    StackItem,
    withBreakpointRange,
} from '@optimus-web/core';
import { isMobile } from '@mews-commander/ui';
import { getCurrentScopeProfileId } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import EntityTitle from '../../../components/commander-ui/EntityTitle';
import { isSeen } from '../utils/isSeen';
import getUnreadCount from '../utils/getUnreadCount';
import { markNotificationsSeen } from '../actions/common';
import { getMappedEnterprise } from '../selectors';
import { ADDITIONAL_VISIBLE_NOTIFICATION_COUNT } from '../constants';
import ShowMoreNotificationsButton from './ShowMoreNotificationsButton';
import { LabelElement, NoContentElement, NoWrapTextButton } from './style';
import NotificationItem from './NotificationItem';
import MessageNotificationContextMenu from './MessageNotificationContextMenu';

const roundToTheNearestTen = (number: $TSFixMe) => (ceil as $TSFixMe).convert({ fixed: false })(number, -1);

interface ScopeNotificationListProps {
    enterprise?: {};
    notifications?: {}[];
    icon?: string;
    title?: React.ReactNode;
    id?: string;
    currentProfileId?: string;
    close?: $TSFixMeFunction;
    handleMarkAllSeen?: $TSFixMeFunction;
    handleOnScopeTitleClicked?: $TSFixMeFunction;
}

type ScopeNotificationListState = $TSFixMe;

class ScopeNotificationList extends Component<ScopeNotificationListProps, ScopeNotificationListState> {
    state = {
        pendingNotificationIds: [],
        // @ts-expect-error $TSFixMeError: Object is possibly 'undefined'.
        visibleNotificationsCount: Math.min(this.props.notifications.length, ADDITIONAL_VISIBLE_NOTIFICATION_COUNT),
    };

    componentDidMount() {
        this.setPendingNotificationIds(this.props.notifications);
    }

    UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
        this.setPendingNotificationIds(nextProps.notifications);
    }

    setPendingNotificationIds = (notifications: $TSFixMe) => {
        const pendingNotificationIds = compose(
            map((n) => (n as $TSFixMe).id),
            reject(isSeen)
        )(notifications);

        this.setState({
            pendingNotificationIds,
        });
    };

    handleMarkAllSeen = () => {
        // @ts-expect-error $TSFixMeError: Cannot invoke an object which is possibly 'undefined'.
        this.props.handleMarkAllSeen(this.state.pendingNotificationIds);
    };

    handleOnScopeTitleClicked = () => {
        // @ts-expect-error $TSFixMeError: Cannot invoke an object which is possibly 'undefined'.
        this.props.close();
        // @ts-expect-error $TSFixMeError: Cannot invoke an object which is possibly 'undefined'.
        this.props.handleOnScopeTitleClicked();
    };

    handleShowMoreNotifications = () => {
        const { visibleNotificationsCount } = this.state;
        const { notifications } = this.props;

        const nextVisibleNotificationCount = Math.min(
            // @ts-expect-error $TSFixMeError: Object is possibly 'undefined'.
            notifications.length,
            visibleNotificationsCount + ADDITIONAL_VISIBLE_NOTIFICATION_COUNT
        );

        this.setState({
            visibleNotificationsCount: nextVisibleNotificationCount,
        });
    };

    getRemainingUnreadNotificationsCount = (visibleNotifications: $TSFixMe) => {
        const unreadNotificationsCount = getUnreadCount(this.props.notifications);
        const visibleUnreadNotificationsCount = getUnreadCount(visibleNotifications);
        return unreadNotificationsCount - visibleUnreadNotificationsCount;
    };

    shouldHighlightItem = (index: $TSFixMe) => {
        const prevVisibleNotificationsCount = roundToTheNearestTen(
            this.state.visibleNotificationsCount - ADDITIONAL_VISIBLE_NOTIFICATION_COUNT
        );
        const itemOrderValue = index + 1;
        return prevVisibleNotificationsCount !== 0 && itemOrderValue > prevVisibleNotificationsCount;
    };

    createNotificationItem = (notification: $TSFixMe, index: $TSFixMe) => (
        <NotificationItem
            key={notification.id}
            {...notification}
            close={this.props.close}
            highlight={this.shouldHighlightItem(index)}
        />
    );

    renderNotificationItems = (visibleNotifications: $TSFixMe) => visibleNotifications.map(this.createNotificationItem);

    render() {
        const { pendingNotificationIds, visibleNotificationsCount } = this.state;
        const {
            enterprise,
            notifications,
            icon,
            title,
            currentProfileId,
            // @ts-expect-error $TSFixMeError: Property 'profileId' does not exist on type 'Readonly<ScopeNotificationListProps> & Readonly<{ children?: ReactNode; }...
            profileId,
            // @ts-expect-error $TSFixMeError: Property 'breakpointRange' does not exist on type 'Readonly<ScopeNotificationListProps> & Readonly<{ children?: ReactN...
            breakpointRange,
            handleOnScopeTitleClicked,
            // @ts-expect-error $TSFixMeError: Property 'showMessageContextMenu' does not exist on type 'Readonly<ScopeNotificationListProps> & Readonly<{ children?:...
            showMessageContextMenu,
        } = this.props;

        const isPhone = isMobile(breakpointRange);
        const isCurrentProfile = currentProfileId === profileId;

        if (isEmpty(notifications) && !isCurrentProfile) {
            return null;
        }

        const pendingNotificationsCount = pendingNotificationIds.length;
        const hasPendingNotifications = pendingNotificationsCount > 0;
        const badgeCount = formatCountToMaxNinetyNine(pendingNotificationsCount);
        const visibleNotifications = take(visibleNotificationsCount, notifications);
        const remainingUnreadNotificationsCount = this.getRemainingUnreadNotificationsCount(visibleNotifications);
        const visibleNotificationsItems = this.renderNotificationItems(visibleNotifications);
        const markAllReadButton = isPhone ? (
            <IconButton icon={IconName.MarkAsRead} onClick={this.handleMarkAllSeen} variant="ghost" type="submit" />
        ) : (
            <NoWrapTextButton icon={IconName.MarkAsRead} onClick={this.handleMarkAllSeen} type="submit">
                <LocalizedText textKey={TextKey.MarkAllRead} />
            </NoWrapTextButton>
        );
        const markAllReadButtonOrNull = hasPendingNotifications ? markAllReadButton : null;

        const handleTitleClicked = handleOnScopeTitleClicked && this.handleOnScopeTitleClicked;

        return (
            <div data-test-profile-notification-list={profileId}>
                <LabelElement>
                    <Stack itemAlignment="center" spacing="100">
                        <EntityTitle
                            icon={isCurrentProfile ? icon : ''}
                            imageUrl={!isCurrentProfile ? (enterprise as $TSFixMe).imageUrl : ''}
                        >
                            {!isCurrentProfile ? (enterprise as $TSFixMe).name : title}
                        </EntityTitle>
                        {pendingNotificationsCount > 0 ? (
                            <StackItem>
                                <Badge content={badgeCount} />
                            </StackItem>
                        ) : null}
                    </Stack>
                    {showMessageContextMenu ? (
                        <MessageNotificationContextMenu
                            onViewAllMessagesClick={handleTitleClicked}
                            onMarkAllSeenClick={this.handleMarkAllSeen}
                            showMarkAllSeenMenuItem={hasPendingNotifications}
                        />
                    ) : (
                        markAllReadButtonOrNull
                    )}
                </LabelElement>
                {!isEmpty(notifications) ? (
                    <Fragment>
                        {visibleNotificationsItems}
                        <ShowMoreNotificationsButton
                            data-test-more-notifications-button
                            // @ts-expect-error $TSFixMeError: Object is possibly 'undefined'.
                            hide={notifications.length - visibleNotificationsCount === 0}
                            onClick={this.handleShowMoreNotifications}
                            remainingUnreadNotificationsCount={remainingUnreadNotificationsCount}
                        />
                    </Fragment>
                ) : (
                    <NoContentElement>
                        <LocalizedText textKey={TextKey.NothingToBeDone} />
                    </NoContentElement>
                )}
            </div>
        );
    }
}

export default compose(
    connect(
        // @ts-expect-error $TSFixMeError: Property 'profileId' does not exist on type '{}'.
        (state, { profileId, enterpriseId, scopeNotificationSelector }) => ({
            notifications: scopeNotificationSelector(state, { profileId }),
            // @ts-expect-error $TSFixMeError: Argument of type '{}' is not assignable to parameter of type 'ApplicationState'.
            enterprise: getMappedEnterprise(state, { enterpriseId }),
            currentProfileId: getCurrentScopeProfileId(state),
        }),
        // @ts-expect-error $TSFixMeError: Property 'profileId' does not exist on type '{}'.
        (dispatch, { profileId, onScopeTitleAction }) => ({
            // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe, { container }: $TSFixMe) => any' is not assignable to paramet...
            handleMarkAllSeen: (ids: $TSFixMe) => dispatch(markNotificationsSeen(ids, profileId)),
            handleOnScopeTitleClicked: onScopeTitleAction ? () => dispatch(onScopeTitleAction(profileId)) : null,
        })
    ),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    withBreakpointRange
    // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
)(ScopeNotificationList);
